import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Amplify } from "aws-amplify";
import {
  confirmSignIn,
  getCurrentUser,
  signIn,
  signOut as amplifySignOut,
  type AuthUser,
} from "aws-amplify/auth";
import {
  Button,
  Callout,
  Card,
  Container,
  Flex,
  Heading,
  Text,
  TextField,
} from "@radix-ui/themes";

/**
 * Configure Amplify Auth against the Cognito user pool.
 *
 * Values are sourced from `VITE_PUBLIC_*` environment variables so they are
 * available to the client bundle. See `.env.local` for the expected keys.
 *
 * The user pool uses passwordless SMS OTP as the first auth factor, so the
 * login UI only collects a phone number and the one-time code delivered via
 * SMS.
 */
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_PUBLIC_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_PUBLIC_COGNITO_CLIENT_ID,
      loginWith: {
        phone: true,
      },
    },
  },
});

type AuthContextValue = {
  /** The signed-in Cognito user, or `null` when unauthenticated. */
  user: AuthUser | null;
  /** `true` once the initial session check has completed. */
  ready: boolean;
  /** Signs the current user out and clears local auth state. */
  signOut: () => Promise<void>;
  /** Re-reads the current session from Cognito. */
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Reads Cognito auth state. Must be called inside a `CognitoAuthProvider`.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within a CognitoAuthProvider");
  }
  return ctx;
}

/**
 * Provides Cognito-backed auth state to a subtree. This is intentionally scoped
 * to the routes that require authentication rather than the whole app, since
 * every other page is unauthenticated (and prerendered).
 */
export function CognitoAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setUser(await getCurrentUser());
    } catch {
      setUser(null);
    }
  }, []);

  const signOut = useCallback(async () => {
    await amplifySignOut();
    setUser(null);
  }, []);

  useEffect(() => {
    void refresh().finally(() => setReady(true));
  }, [refresh]);

  const value = useMemo(
    () => ({ user, ready, signOut, refresh }),
    [user, ready, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Gate that ensures the user is authenticated before rendering `children`.
 * Unauthenticated users are shown a passwordless sign-in UI (phone number +
 * SMS one-time code). Must be rendered inside a `CognitoAuthProvider`.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, ready, refresh } = useAuth();

  if (!ready) {
    return null;
  }

  if (!user) {
    return <SmsOtpSignIn onSignedIn={refresh} />;
  }

  return <>{children}</>;
}

/**
 * Normalizes user-entered phone input into E.164 form. US-centric: a bare
 * 10-digit number is prefixed with `+1`, an 11-digit number starting with `1`
 * gets a leading `+`, and anything already starting with `+` is preserved.
 */
function toE164(input: string): string {
  const trimmed = input.trim();
  if (trimmed.startsWith("+")) {
    return "+" + trimmed.slice(1).replace(/\D/g, "");
  }
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) {
    return "+1" + digits;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return "+" + digits;
  }
  return "+" + digits;
}

/**
 * Passwordless sign-in form using Cognito's `USER_AUTH` flow with the
 * `SMS_OTP` challenge. Collects a phone number, then the one-time code.
 */
function SmsOtpSignIn({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submitPhone = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      let { nextStep } = await signIn({
        username: toE164(phone),
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "SMS_OTP",
        },
      });

      // When the pool allows multiple first auth factors (e.g. password and
      // SMS OTP), Cognito asks us to choose one before issuing the challenge.
      // Select SMS_OTP to continue the passwordless flow.
      if (
        nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION"
      ) {
        ({ nextStep } = await confirmSignIn({ challengeResponse: "SMS_OTP" }));
      }

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
        setStep("code");
      } else if (nextStep.signInStep === "DONE") {
        await onSignedIn();
      } else {
        setError(`Unexpected sign-in step: ${nextStep.signInStep}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send a code.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { nextStep } = await confirmSignIn({
        challengeResponse: code.trim(),
      });
      if (nextStep.signInStep === "DONE") {
        await onSignedIn();
      } else {
        setError("Unexpected confirmation step. Please try again.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container size="1" className="route-page" maxWidth="420px">
      <Card>
        <Flex direction="column" gap="4" p="2">
          <Heading size="5">Sign in</Heading>
          {step === "phone" ? (
            <form onSubmit={submitPhone}>
              <Flex direction="column" gap="3">
                <Text size="2" color="gray">
                  Enter your phone number and we&apos;ll text you a one-time
                  code.
                </Text>
                <TextField.Root
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+1 555 123 4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  size="3"
                />
                {error && (
                  <Callout.Root color="red" size="1">
                    <Callout.Text>{error}</Callout.Text>
                  </Callout.Root>
                )}
                <Button
                  type="submit"
                  size="3"
                  disabled={submitting || phone.trim().length === 0}
                >
                  {submitting ? "Sending…" : "Send code"}
                </Button>
              </Flex>
            </form>
          ) : (
            <form onSubmit={submitCode}>
              <Flex direction="column" gap="3">
                <Text size="2" color="gray">
                  Enter the code we sent to {toE164(phone)}.
                </Text>
                <TextField.Root
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  size="3"
                />
                {error && (
                  <Callout.Root color="red" size="1">
                    <Callout.Text>{error}</Callout.Text>
                  </Callout.Root>
                )}
                <Button
                  type="submit"
                  size="3"
                  disabled={submitting || code.trim().length === 0}
                >
                  {submitting ? "Verifying…" : "Verify"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  color="gray"
                  size="2"
                  onClick={() => {
                    setStep("phone");
                    setCode("");
                    setError(null);
                  }}
                >
                  Use a different number
                </Button>
              </Flex>
            </form>
          )}
        </Flex>
      </Card>
    </Container>
  );
}
